import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, getSelectedOrgId, type Organization, type Service, type ServiceHealth } from "../api";
import { Icons } from "../components/icons";
import { ServiceCreateModal } from "../components/ServiceCreateModal";
import { useAuth } from "../auth";
import {
  useGetServicesQuery,
  useGetServicesSummaryQuery,
  useDeleteServiceMutation,
} from "../store/services";

type ServiceWithHealth = Service & { health?: ServiceHealth | null };

const CATEGORY_ICON: Record<string, keyof typeof Icons> = {
  pagos: "Zap",
  identidad: "Shield",
  notificaciones: "Mail",
  documentos: "Cube",
  plataforma: "Activity",
  blockchain: "Cube",
};

export default function Services() {
  const [urlParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [orgsMap, setOrgsMap] = useState<Record<string, Organization>>({});
  const [orgsList, setOrgsList] = useState<Organization[]>([]);
  void urlParams.get("org_id"); // legacy; el filtro real ya lo aplica el backend via header X-Org-Id.
  const { user } = useAuth();
  const canCreate = (user?.roles ?? []).includes("admin");

  // RTK Query: servicios + summary con cache compartido + refetchOnFocus automático.
  // El polling de 15s legacy se reemplaza por `pollingInterval` nativo de RTK.
  const { data: services = [], refetch: refetchServices } = useGetServicesQuery(
    { expand: "health" },
    { pollingInterval: 15_000 },
  );
  const { data: summary } = useGetServicesSummaryQuery(undefined, { pollingInterval: 15_000 });
  const [deleteServiceMutation] = useDeleteServiceMutation();

  const fetchOrgs = useCallback(() => {
    if (!user?.is_platform_admin) return;
    apiGet<{ organizations: Organization[] }>("/v1/organizations")
      .then((r) => {
        setOrgsList(r.organizations);
        const m: Record<string, Organization> = {};
        for (const o of r.organizations) m[o.id] = o;
        setOrgsMap(m);
      })
      .catch(() => {
        setOrgsMap({});
        setOrgsList([]);
      });
  }, [user?.is_platform_admin]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // Cuando el usuario cambia de org en el header, RTK Query no se entera solo
  // (el cache está keyed por endpoint+args, no por header X-Org-Id). Forzamos refetch.
  useEffect(() => {
    function onOrgChanged() {
      refetchServices();
      fetchOrgs();
    }
    window.addEventListener("sovra:org-changed", onOrgChanged);
    return () => window.removeEventListener("sovra:org-changed", onOrgChanged);
  }, [refetchServices, fetchOrgs]);

  function handleCreated() {
    // `createService` (cuando lo migremos) invalidará el tag "Services" y la lista
    // refrescará sola. Por ahora, mientras ServiceCreateModal sigue usando apiPost legacy,
    // forzamos un refetch manual.
    refetchServices();
  }

  async function handleDelete(svc: Service) {
    const ok = window.confirm(
      `¿Eliminar el servicio "${svc.name}" (${svc.slug})?\n\n` +
        `Los traces históricos NO se borran, pero el servicio dejará de aparecer y nuevos eventos contra "${svc.slug}" serán rechazados.`,
    );
    if (!ok) return;
    // RTK Query maneja el optimistic + rollback automático via `invalidatesTags`.
    // Si el delete falla, la lista se refresca al estado del server.
    try {
      // Pasamos uuid en vez de slug — el slug puede no ser único cross-org
      // y el backend respondería 400 ambiguous_service_slug en admin global.
      await deleteServiceMutation(svc.id).unwrap();
    } catch (err) {
      console.error("Error eliminando servicio:", err);
      alert("No se pudo eliminar el servicio.");
    }
  }

  return (
    <>
      <header className="page-header">
        <div>
          <h1>Servicios</h1>
          <div className="sub">
            Endpoints externos invocados por las Cloud Functions · status derivado de ingesta pasiva
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
          {summary && (
            <div style={{ display: "flex", gap: 18, fontSize: 12.5, color: "var(--fg-2)" }}>
              <Stat
                label="operativos"
                value={summary.operational}
                accent="ok"
                help="Servicios respondiendo OK (no degradados, sin error_rate alto en los últimos 5 min). Verde."
              />
              <Stat
                label="degradados"
                value={summary.degraded}
                accent="degraded"
                help="Servicios con latencia elevada (p95 sobre el umbral) o error_rate &gt; 5% en los últimos 5 min. Amarillo."
              />
              <Stat
                label="caídas"
                value={summary.major}
                accent="major"
                help="Servicios con error_rate &gt; 25% o timeouts mayoritarios. Equivale a 'rojo' / no operativo. Rojo."
              />
              <Stat
                label="p95"
                value={`${summary.p95_latency_global} ms`}
                help="Latencia del percentil 95 global (de todos los servicios). 95% de los requests terminaron por debajo de este número."
              />
              <Stat
                label="error rate"
                value={`${(summary.error_rate_global * 100).toFixed(1)}%`}
                help="Porcentaje de events con level='error' sobre el total de la ventana. Acumulado de todos los servicios."
              />
            </div>
          )}
          {canCreate && (
            <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              <Icons.Plus size={13} sw={2.2} />
              <span style={{ marginLeft: 6 }}>Crear servicio</span>
            </button>
          )}
        </div>
      </header>

      {services.length === 0 ? (
        <EmptyState canCreate={canCreate} onCreate={() => setCreateOpen(true)} />
      ) : (
        <div className="services-grid">
          {services.map((s) => (
            <ServiceCard
              key={s.id}
              service={s}
              orgName={
                user?.is_platform_admin && getSelectedOrgId() == null && s.org_id
                  ? (orgsMap[s.org_id]?.name ?? s.org_id)
                  : undefined
              }
              onDelete={canCreate ? handleDelete : undefined}
            />
          ))}
        </div>
      )}

      <ServiceCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        showOrgPicker={user?.is_platform_admin === true && getSelectedOrgId() == null}
        orgs={orgsList}
      />
    </>
  );
}

function EmptyState({ canCreate, onCreate }: { canCreate: boolean; onCreate: () => void }) {
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        padding: "64px 24px",
        textAlign: "center",
        gap: 16,
        background: "var(--bg-1)",
        border: "1px dashed var(--border-1)",
        borderRadius: 8,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: "var(--bg-2)",
          display: "grid",
          placeItems: "center",
          color: "var(--fg-3)",
        }}
      >
        <Icons.Cube size={22} />
      </div>
      <div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700 }}>
          No hay servicios registrados
        </div>
        <p className="text-sm text-muted" style={{ maxWidth: 460, marginTop: 6 }}>
          Registrá los sistemas externos que vas a observar (Renaper, MercadoPago, tu API
          interna, etc.). Después tus clientes pueden mandar events de telemetría que se
          agregan acá.
        </p>
      </div>
      {canCreate && (
        <button type="button" className="btn btn-primary" onClick={onCreate}>
          <Icons.Plus size={13} sw={2.2} />
          <span style={{ marginLeft: 6 }}>Crear primer servicio</span>
        </button>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  help,
}: {
  label: string;
  value: string | number;
  accent?: string;
  help?: string;
}) {
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 80, position: "relative" }}
      className="stat-cell"
      tabIndex={help ? 0 : undefined}
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 600,
          color: "var(--fg-3)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          cursor: help ? "help" : "default",
        }}
      >
        {label}
        {help && (
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 12,
              height: 12,
              borderRadius: "50%",
              border: "1px solid var(--border-1)",
              color: "var(--fg-4)",
              fontSize: 9,
              fontWeight: 600,
              fontFamily: "var(--font-sans)",
            }}
          >
            i
          </span>
        )}
      </span>
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 20,
          letterSpacing: "-0.02em",
          color: accent ? `var(--st-${accent})` : "var(--fg-1)",
        }}
      >
        {value}
      </span>
      {help && <span className="stat-tooltip" role="tooltip" dangerouslySetInnerHTML={{ __html: help }} />}
    </div>
  );
}

function ServiceCard({
  service,
  orgName,
  onDelete,
}: {
  service: ServiceWithHealth;
  orgName?: string;
  onDelete?: (s: Service) => void;
}) {
  // Linkeamos por `service.id` (uuid) — globalmente único, sin riesgo de
  // colisión cuando el mismo slug existe en >1 org. El backend acepta
  // uuid o slug en `/v1/services/:id` y deriva la org de la fila del service.
  const linkTo = `/services/${encodeURIComponent(service.id)}`;
  const status = service.health?.status ?? "unknown";
  const statusLabel: Record<string, string> = {
    ok: "Operativo",
    degraded: "Degradado",
    partial: "Parcial",
    major: "Caída mayor",
    maintenance: "Mantenimiento",
    unknown: "Sin datos",
  };

  const iconName = CATEGORY_ICON[service.category] ?? "Cube";
  const IconComp = Icons[iconName];

  // Stop el navigate del <Link> contenedor cuando se hace click en el botón eliminar.
  function handleDeleteClick(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.(service);
  }

  return (
    <Link to={linkTo} className={`svc-card ${status}`}>
      <div className="svc-card-head">
        <div className="svc-card-icon">
          <IconComp size={17} sw={1.7} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {orgName && (
            <span className="svc-card-org" title={orgName}>
              <span className="svc-card-org-dot" aria-hidden="true" />
              <span className="svc-card-org-name">{orgName}</span>
            </span>
          )}
          <div className="svc-card-name" title={service.name}>{service.name}</div>
          <div className="svc-card-sub">
            {service.category} · {service.health?.requests_24h ?? 0} req/24h
          </div>
        </div>
        <div className="svc-card-actions">
          <span className={`badge badge-${status}`}>
            <span className="dot" style={{ background: "currentColor" }} />
            {statusLabel[status]}
          </span>
          {onDelete && (
            <button
              type="button"
              className="btn btn-icon btn-ghost svc-delete-btn"
              onClick={handleDeleteClick}
              title={`Eliminar ${service.slug}`}
              aria-label={`Eliminar ${service.slug}`}
              style={{ width: 26, height: 26, color: "var(--st-degraded)" }}
            >
              <Icons.Trash size={13} />
            </button>
          )}
        </div>
      </div>

      <div className="svc-card-metrics">
        <div className="metric-block">
          <span className="k">Latencia p95</span>
          <span className="v">
            {service.health?.latency_p95_ms != null
              ? service.health.latency_p95_ms < 1000
                ? `${service.health.latency_p95_ms}ms`
                : `${(service.health.latency_p95_ms / 1000).toFixed(1)}s`
              : "—"}
          </span>
        </div>
        <div className="metric-block">
          <span className="k">Error 24h</span>
          <span className="v" style={{ color: (service.health?.error_rate_24h ?? 0) > 0.1 ? "var(--st-major)" : "var(--fg-1)" }}>
            {service.health?.error_rate_24h != null
              ? `${Math.round(service.health.error_rate_24h * 100)}%`
              : "—"}
          </span>
        </div>
      </div>
    </Link>
  );
}
